import React, { useState } from 'react';
import IconButton from '@mui/material/IconButton';
import MoreHorizIcon from '@mui/icons-material/MoreHorizOutlined';
import { StyledMenuList } from 'framework/ui/menu';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import PersonTimelineRecordAnchor from './PersonTimelineRecordAnchor';
import { CANCEL_INAPPROPRIATE_FLAGGING_LABEL, INAPPROPRIATE_FLAGGING_LABEL } from '../../constants';
import { Box } from '@mui/material';
import { dropdownMenuPaperSx } from 'framework/ui/styles';

interface PersonTimelineRecordActionsProps {
  buttons?: React.ReactElement[];
  actions?: React.ReactElement[];
  timelineId: StringOrNumber;
  showLinkButton?: boolean;
  basePathname?: string;
}

const CLOSE_MODAL_BUTTONS = ['Cancel', '×'];
const NON_TOGGLE_DROPDOWN_BUTTONS = [INAPPROPRIATE_FLAGGING_LABEL, 'Delete', 'Submit', 'Transfer', 'Add to blacklist'];
const MODAL_INTERNAL_CLASSES = ['modal-content', 'modal-body', 'form-check', 'flag-dropdown', 'input-group'];

const PersonTimelineRecordActions = ({
  buttons,
  actions,
  timelineId,
  showLinkButton = true,
  basePathname = '',
}: PersonTimelineRecordActionsProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isDropdownHidden, setIsDropdownHidden] = useState(false);
  const id = `timeline-actions-${timelineId}`;

  const isClickInsideModal = (parentOffset?: any) => {
    if (!parentOffset) return false;

    if (MODAL_INTERNAL_CLASSES.some(className => parentOffset?.className?.includes(className))) {
      return true;
    }

    return isClickInsideModal(parentOffset?.offsetParent);
  };

  const toggleDropdown = ({ target: { offsetParent, innerText } }: any) => {
    const text = innerText?.replace(/\n/gi, '');
    const isCancelFlagging = text === CANCEL_INAPPROPRIATE_FLAGGING_LABEL;
    const isCloseModalButton = CLOSE_MODAL_BUTTONS.includes(text);
    const isDropdownModalOpen = NON_TOGGLE_DROPDOWN_BUTTONS.includes(text);
    const isClickOnDropdownModal = isClickInsideModal(offsetParent);
    const isClickOutsideModal = isDropdownHidden && !isClickOnDropdownModal;
    const isCloseModalButtonClick = isClickOnDropdownModal && isCloseModalButton;
    const shouldToggleDropdown = !isClickOnDropdownModal && !isDropdownModalOpen && !isCancelFlagging;

    if (isDropdownModalOpen || isClickOutsideModal || isCloseModalButtonClick) {
      setIsDropdownHidden(!isDropdownHidden);
    }

    if (isCloseModalButtonClick || shouldToggleDropdown) {
      setIsDropdownOpen(!isDropdownOpen);

      if (isDropdownModalOpen) {
        setAnchorEl(null);
      }
    }
  };

  const getButtonDropdown = () => {
    return (
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        <IconButton
          id={id}
          sx={{
            padding: '1px',
            marginRight: '12px',
            '& .MuiSvgIcon-root': { width: '18px', height: '18px' },
          }}
          aria-controls={isDropdownOpen ? id : undefined}
          aria-haspopup="true"
          aria-expanded={isDropdownOpen ? 'true' : undefined}
          onClick={e => {
            if (isDropdownOpen) return;

            setAnchorEl(e.currentTarget);
            toggleDropdown(e);
          }}
        >
          <MoreHorizIcon />
        </IconButton>
        <Popper
          open={isDropdownOpen}
          anchorEl={anchorEl}
          placement="bottom-end"
          transition
          disablePortal
          sx={{ zIndex: 1 }}
        >
          {({ TransitionProps }) => (
            <Grow {...TransitionProps}>
              <Paper
                elevation={3}
                className={`actions-dropdown-menu ${isDropdownHidden ? 'is-hidden' : ''}`}
                sx={{ ...dropdownMenuPaperSx, visibility: isDropdownHidden ? 'hidden' : 'visible' }}
              >
                <ClickAwayListener onClickAway={toggleDropdown}>
                  <StyledMenuList
                    autoFocusItem={false}
                    id={id}
                    aria-labelledby="composition-button"
                    onClick={toggleDropdown}
                  >
                    {buttons}
                  </StyledMenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </Box>
    );
  };

  return (
    <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'row', paddingRight: '4px' }}>
      {actions?.length ? actions : null}
      {buttons?.length ? getButtonDropdown() : null}
      {timelineId && showLinkButton && (
        <PersonTimelineRecordAnchor timelineId={timelineId} basePathname={basePathname} />
      )}
    </Box>
  );
};

export default PersonTimelineRecordActions;
